import React, { useRef, useState, useEffect } from "react";
import * as Yup from "yup";
import {
  ScopeCertificateDetails,
  StandardDetails,
} from "../../../sc-models/scope-certificate-details-dto";
import FormBuilderV4 from "../../../../../../shared/components/form-builder/form-builder-v4";
import { OptionType } from "../../../../../../shared/components/form-builder/form-config.model";
import { useScStore } from "../../../../../../store/scStandard.store";
import { useParams } from "react-router-dom";
import { notify } from "../../../../../../shared/utils/notify.utills";
import { dtrackItServiceV4 } from "../../../sc-service/dtrackit.service";
import { RestrictedDataValidation } from "../../../../../../shared/utils/custom-validation.utils";
import { InputAdornment, Box, Alert, AlertTitle } from "@mui/material";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";

interface ScopeCertificateDetailsFormProps {
  formRef: any;
  detailsData: ScopeCertificateDetails;
  setDetailsData: React.Dispatch<React.SetStateAction<ScopeCertificateDetails>>;
  SC_ID?: string;
  isAmendment?: boolean;
  scValidFromDate?: string | number;
  scStatus?: string;
  hasPR0035?: boolean;
  hasFarmProcessCodes?: boolean;
  isSuspended?: boolean;
  isFarmSC?: boolean;
  setIsFarmSC?: (val: boolean) => void;
  isLocked?: boolean;
  setIsFormDirty?: (val: boolean) => void;
}

const ScopeCertificateDetailsForm: React.FC<
  ScopeCertificateDetailsFormProps
> = ({
  formRef,
  detailsData,
  setDetailsData,
  SC_ID,
  isAmendment,
  scValidFromDate,
  scStatus,
  hasPR0035,
  hasFarmProcessCodes,
  isSuspended,
  isFarmSC,
  setIsFarmSC,
  isLocked,
  setIsFormDirty,
}) => {
  const { editSCInfo, scStandards } = useScStore();
  const { formtype } = useParams();
  const hasSubmitSC: boolean = formtype === "submited-sc";
  const [approvedClaimsOptions, setApprovedClaimsOptions] = useState<OptionType[]>([]);
  const [loading, setLoading] = useState(true);
  const { cbLicensingCode } = useTransactionCertificateStore();
  const shortCode = cbLicensingCode?.replace(/^CB-/, "") || "";

  // M4: Expired/Withdrawn SCs are fully read-only
  const isStatusLocked = ['E', 'W'].includes(scStatus || '');

  // Check if scNumberOfFarms field should be enabled based on selected standards
  const farmRelatedStandards = ["RWS", "RMS", "RAS", "RDS", "MMS"];
  const hasFarmRelatedStandards = scStandards.some((standard) =>
    farmRelatedStandards.includes(standard)
  );

  useEffect(() => {
    if (detailsData?.isFarmingAreaOnly) {
      useScStore.getState().setIsFarmingAreaOnly(detailsData.isFarmingAreaOnly === 'Y');
    }
  }, [detailsData?.isFarmingAreaOnly]);

  // Fetch dropdown options from API
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        setLoading(true);

        // Fetch Certified Organization Approved Claims values (SCCLMSV4)
        const approvedClaimsResponse = await dtrackItServiceV4.getDTrackItRefCodes('SCCLMSV4');
        if (approvedClaimsResponse?.data) {
          const transformedClaimsOptions = approvedClaimsResponse.data.map((item: any) => ({
            label: `${item.description_1} (${item.code_1})`,
            value: item.code_1,
          }));
          setApprovedClaimsOptions(transformedClaimsOptions);
        }

      } catch (error) {
        console.error("Error fetching dropdown options:", error);
        notify("Failed to load dropdown options", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchDropdownOptions();
  }, []);

  const isNaLike = (v?: string | null) => {
    if (!v) return false;
    const lower = v.trim().toLowerCase();
    return ["na", "n/a", "na.", "na ", "not available", "null", "nil", "none", "n a", "n.a"].includes(lower);
  };

  const naOnlyValidator = (value?: string | null) => {
    if (!value) return true;
    const lower = value.trim().toLowerCase();
    if (!isNaLike(value)) return true;
    return lower === "na";
  };

  const normalizeNaVariant = (value: string | undefined | null) => {
    if (!value) return value;
    return isNaLike(value) ? 'na' : value;
  };

  const formConfig = {
    pageName: "scope-certificate-details",
    initialValues: detailsData,
    enableReinitialize: true,
    onValuesChange: (values: any) => {
      const hasChanges = JSON.stringify(values) !== JSON.stringify(detailsData);
      if (setIsFormDirty) setIsFormDirty(hasChanges);
      const currentErrors = useScStore.getState().backendValidationErrors['scope-certificate-details'] || [];
      if (currentErrors.length === 0) return;

      let updatedErrors = [...currentErrors];

      // Check brandName
      const brandNames = values.brandName || [];
      if (brandNames.length > 0) {
        updatedErrors = updatedErrors.filter(
          (err) =>
            !err.toLowerCase().includes("brandname") &&
            !err.toLowerCase().includes("brand name")
        );
      }

      // Check certifiedOrganizationApprovedClaims
      const approvedClaims = values.certifiedOrganizationApprovedClaims;
      if (approvedClaims && approvedClaims.trim() !== "" && approvedClaims.trim().toUpperCase() !== "NONE") {
        updatedErrors = updatedErrors.filter(
          (err) =>
            !err.toLowerCase().includes("certifiedorganization") &&
            !err.toLowerCase().includes("approved claims") &&
            !err.toLowerCase().includes("approvedclaims")
        );
      }

      // Check scNumberOfFarms
      const scNumberOfFarms = values.scNumberOfFarms;
      if (scNumberOfFarms !== null && scNumberOfFarms !== undefined && scNumberOfFarms >= 1) {
        updatedErrors = updatedErrors.filter(
          (err) =>
            !err.toLowerCase().includes("numberoffarms") &&
            !err.toLowerCase().includes("number of farms")
        );
      }

      // Check scLegacyNo
      const scLegacyNo = values.scLegacyNo;
      if (scLegacyNo && scLegacyNo.trim() !== "") {
        updatedErrors = updatedErrors.filter(
          (err) =>
            !err.toLowerCase().includes("legacyno") &&
            !err.toLowerCase().includes("legacy number")
        );
      }

      // Check scopeCertificateNo
      const scopeCertificateNo = values.scopeCertificateNo;
      if (scopeCertificateNo && scopeCertificateNo.trim() !== "" && scopeCertificateNo.length === 19) {
        updatedErrors = updatedErrors.filter(
          (err) =>
            !err.toLowerCase().includes("scopecertificateno") &&
            !err.toLowerCase().includes("scope certificate number") &&
            !err.toLowerCase().includes("sc number")
        );
      }

      if (updatedErrors.length !== currentErrors.length) {
        useScStore.getState().setBackendValidationErrors('scope-certificate-details', updatedErrors);
      }
    },
    validationSchema: Yup.object().shape({
      scopeCertificateNo: Yup.string()
        .required("Scope Certificate Number is required")
        .length(19, "Must be exactly 19 characters (e.g. SC-########-YYMM-00)")
        .matches(/^SC-\d{8}-\d{4}-00$/, "Invalid format (XXX-SC-########-YYMM-00)"),
      brandName: Yup.array()
        .of(Yup.string().max(100, "Max 100 characters per brand"))
        .when([], {
          is: () => hasPR0035,
          then: (schema) => schema.min(1, "Brand Name is required when process code PR0035 is present"),
          otherwise: (schema) => schema.optional(),
        }),
      gotsSCNo: Yup.string().max(100, "Max 100 characters"),
      scLegacyNo: Yup.string()
        .transform((value) => {
          if (typeof value !== "string") return value;
          const trimmed = value.trim();
          if (["na", "NA", "Na", "nA"].includes(trimmed)) {
            return "na";
          }
          return value;
        })
        .required("SC Legacy Number is required")
        .max(100, "Max 100 characters")
        .test("no-whitespace", "Whitespace-only value is not allowed in Legacy Number", (v) => {
          if (!v) return true;
          return v.trim().length > 0;
        })
        .test(
          "no-notavailable",
          "Variation of Not Available is not allowed",
          (value) => {
            if (!value) return true;
            const cleaned = value.toLowerCase().replace(/\s+/g, "");
            return cleaned !== "notavailable";
          }
        )
        .test(
          "no-none-variant",
          "None variants are not allowed",
          (value) => {
            if (!value) return true;
            const cleaned = value.toLowerCase().replace(/\s+/g, "");
            return cleaned !== "none";
          }
        )
        .test("min-length-or-na", 'Values with 6 or fewer characters are not allowed (except "na")', (v) => {
          if (!v) return true;
          if (v === "na") return true;
          return v.trim().length > 6;
        }),
      scCBAuditorId: Yup.array()
        .of(
          Yup.string()
            .max(10, "Max 10 characters")
            .matches(/^TEU-\d{6}$/, "Format TEU-######")
        ),
      scStandardProgram: Yup.string(),
      isFarmSC: Yup.string()
        .required("Please select whether this Scope Certificate is for a Farm")
        .oneOf(["Y", "N"], "Please select Yes or No"),
      isFarmingAreaOnly: Yup.string().when("isFarmSC", {
        is: "Y",
        then: (schema) => schema.required("Please select whether it involves Farming (area) only").oneOf(["Y", "N"], "Please select Yes or No"),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      scNumberOfFarms: Yup.number()
        .nullable()
        .integer("SC Number of Farms must be a whole number")
        .when(["isFarmingAreaOnly"], {
          is: (isFarmingAreaOnly: string) => isFarmSC && isFarmingAreaOnly === "N",
          then: (schema) =>
            schema
              .required("SC Number of Farms is required when the SC is for a Farm and does not involve Farming (area) only")
              .min(1, "SC Number of Farms must be at least 1 (cannot be 0 or negative)"),
          otherwise: (schema) => schema.optional().min(0, "SC Number of Farms cannot be negative"),
        }),
      scVersionNo: Yup.string()
        .required("SC Version Number is required")
        .length(19, "Must be exactly 19 characters (e.g. SC-########-YYMM-NN)")
        .matches(/^SC-\d{8}-\d{4}-\d{2}$/, "Invalid format. Must be of format (XXX-SC-########-YYMM-NN)"),
      scProductLimitations: Yup.string().max(2000, "Max 2000 characters"),
      ccsFlag: Yup.string().required("Choose whether CCS Applies"),
      stdClaimsPolicy: Yup.string().required(),
      certifiedOrganizationApprovedClaims: Yup.string()
  .nullable()
  .max(4, "Must be at most 4 characters")
  .oneOf(["PC", "CC", "BOTH", "NONE"], "Invalid value")
  .test(
    "approved-claims-required",
    "Certified Organization Approved Claims is required and cannot be \"NONE\" when process code PR0035 is present",
    function (value) {
      if (hasPR0035 && (!value || value.trim() === "" || value.trim().toUpperCase() === "NONE")) {
        return false;
      }
      return true;
    }
  ),
      // globalComment: Yup.string(),
    }),
    // Form is read-only if: submitted view, OR status is Expired/Withdrawn, OR status is Suspended, OR tab is completed
    readOnly: hasSubmitSC || isStatusLocked || Boolean(isSuspended) || isLocked,
    sections: [
      {
        // title: 'Scope Certificate Details',
        fields: [
          {
            label: "SC Number",
            name: "scopeCertificateNo",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            disabled:
              editSCInfo?.scId === SC_ID && editSCInfo?.isParentStatus
                ? true
                : false,
            props: {
              placeholder: "Enter Scope Certificate Number",
              InputProps: {
                startAdornment: (
                  <InputAdornment position="start">
                    <>
                      <strong>{shortCode}-</strong>
                    </>
                  </InputAdornment>
                ),
              },
            },
            events: {
              onChange: (
                updatedValue: any,
                formValues: any,
                setFieldValue: (arg0: string, arg1: any) => void
              ) => {
                let val = updatedValue || "";
                if (!val.startsWith("SC-")) {
                  val = "SC-" + val.replace(/^SC-?|^S-?/i, "");
                }
                setFieldValue("scopeCertificateNo", val);
                setFieldValue("scVersionNo", val);
              },
            },
          },
          {
            label: "SC Version Number",
            name: "scVersionNo",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            helperText: "Only the last 2 digits (NN) can be changed from 00",
            props: {
              placeholder: "Enter SC Version Number",
              InputProps: {
                startAdornment: (
                  <InputAdornment position="start">
                    <>
                      <strong>{shortCode}-</strong>
                    </>
                  </InputAdornment>
                ),
              },
            },
            events: {
              onChange: (
                updatedValue: any,
                formValues: any,
                setFieldValue: (arg0: string, arg1: any) => void
              ) => {
                let val = updatedValue || "";
                if (!val.startsWith("SC-")) {
                  val = "SC-" + val.replace(/^SC-?|^S-?/i, "");
                }
                // Enforce prefix lock: scVersionNo prefix must match scNo prefix (everything except last 2 digits)
                const scNoValue: string = formValues?.scopeCertificateNo || "";
                if (scNoValue && scNoValue.length >= 19) {
                  const scNoPrefix = scNoValue.slice(0, -2); // e.g. "SC-12345678-2608-"
                  if (val.startsWith(scNoPrefix)) {
                    let suffix = val.slice(scNoPrefix.length).replace(/\D/g, '').substring(0, 2);
                    setFieldValue("scVersionNo", `${scNoPrefix}${suffix}`);
                  } else {
                    let suffix = val.replace(scNoPrefix, "").replace(/\D/g, '').slice(-2);
                    if (suffix.length === 0) suffix = '00';
                    setFieldValue("scVersionNo", `${scNoPrefix}${suffix}`);
                  }
                  return;
                }
                setFieldValue("scVersionNo", val);
              },
            },
          },
          {
            label: "GOTS SC Number",
            name: "gotsSCNo",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: false,
            props: { placeholder: "Enter GOTS SC Number" },
          },
          {
            label: "Brand Name",
            name: "brandName",
            type: "chips",
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: (values: any) => Boolean(hasPR0035),
            props: {
              placeholder: "Enter Brand Name(s)",
            },
          },

          {
            label: "SC CB Auditor ID",
            name: "scCBAuditorId",
            type: "chips",
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: false,
            props: {
              placeholder: "Enter CB Auditor ID",
            },
          },
          {
            label: "SC Legacy Number",
            name: "scLegacyNo",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            helperText: 'Enter "na" if SC Legacy Number is not applicable (only one value allowed)',
            props: { placeholder: "Enter SC Legacy Number" },
          },
          // {
          //   label: 'SC Standard Program',
          //   name: 'scStandardProgram',
          //   type: 'select',
          //   gridSize: { xs: 12, md: 6, lg: 4 },
          //   required: false,
          //   props: {
          //     options: [
          //       { label: "None", value: "" },
          //       { label: "Responsible Animal Fiber", value: "RAS,RWS,RAS" },
          //       { label: "Content Claim Standard", value: "CCS" },
          //     ],
          //     placeholder: 'Select Standard Program',
          //   },
          // },
          {
            label: "Is the Scope Certificate for a Farm?",
            name: "isFarmSC",
            type: "radio",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            disabled: !!SC_ID,
            helperText: hasFarmProcessCodes ? "Farm process codes detected — Yes is recommended" : undefined,
            props: {
              options: [
                { value: "Y", label: "Yes" },
                { value: "N", label: "No" },
              ],
            },
            events: {
              onChange: (
                value: any,
                formValues: any,
                setFieldValue: (arg0: string, arg1: any) => void
              ) => {
                if (setIsFarmSC) setIsFarmSC(value === "Y");
                if (value === "N") {
                  setFieldValue("isFarmingAreaOnly", "");
                  setFieldValue("scNumberOfFarms", null);
                }
              },
            },
          },
          {
            label: "Does it involve Farming (area) only?",
            name: "isFarmingAreaOnly",
            type: "radio",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: (values: any) => Boolean(isFarmSC),
            visible: (values: any) => Boolean(isFarmSC),
            props: {
              options: [
                { value: "Y", label: "Yes" },
                { value: "N", label: "No" },
              ],
            },
            events: {
              onChange: (
                value: any,
                formValues: any,
                setFieldValue: (arg0: string, arg1: any) => void
              ) => {
                if (value === "Y") {
                  setFieldValue("scNumberOfFarms", null);
                }
                useScStore.getState().setIsFarmingAreaOnly(value === "Y");
              },
            },
          },
          {
            label: "SC Number of Farms",
            name: "scNumberOfFarms",
            type: "number",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: (values: any) => Boolean(isFarmSC && values.isFarmingAreaOnly === "N"),
            visible: (values: any) => Boolean(isFarmSC && values.isFarmingAreaOnly === "N"),
            props: {
              placeholder: "Enter Number of Farms",
            },
            events: {
              onChange: (
                value: any,
                formValues: any,
                setFieldValue: (field: string, value: any) => void
              ) => {
                const numericValue = value === "" ? null : parseInt(value, 10);
                setFieldValue("scNumberOfFarms", isNaN(numericValue as number) ? null : numericValue);
              },
            },
          },
          {
            label: "SC Product Limitations",
            name: "scProductLimitations",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: false,
            props: {
              placeholder: "Enter Product Limitations",
            },
          },
          {
            label: "Certified Organization Approved Claims",
            name: "certifiedOrganizationApprovedClaims",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: Boolean(hasPR0035),
            props: {
              options: approvedClaimsOptions,
              placeholder: "Select Approved Claims",
            },
          },
          {
            label: "Does CCS Apply",
            name: "ccsFlag",
            type: "radio",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            props: {
              options: [
                { value: "Y", label: "Yes" },
                { value: "N", label: "No" },
              ],
            },
          },
          {
            label: "Textile Exchange Standards Claims Policy",
            name: "stdClaimsPolicy",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            props: {
              placeholder: "Select the version number",
              options: [
                { value: "V1.3", label: "V1.3" },
                { value: "V1.4", label: "V1.4" },
              ],
            },
          },
          // {
          //   label: 'Global Comment Not For Actual Data To Be Provided',
          //   name: 'globalComment',
          //   type: 'text',
          //   gridSize: { xs: 12, md: 6, lg: 4 },
          //   required: false,
          //   props: { placeholder: 'Enter Global Comment' },
          // },
          // Removed: certifiedOrganizationApprovedClaims field - moved to Standard Details tab
        ],
      },
    ],
    handleSubmit: (values: any) => {
      // Normalize scLegacyNo: trim and normalize na variants to lowercase 'na'
      const legacyNo = typeof values.scLegacyNo === 'string' ? values.scLegacyNo.trim() : values.scLegacyNo;
      const normalizedLegacyNo = ['na', 'NA', 'Na', 'nA'].includes(legacyNo) ? 'na' : legacyNo;
      setDetailsData({ ...values, scLegacyNo: normalizedLegacyNo });
    },
  };

  // Delay rendering until both the dropdown options have loaded AND detailsData
  // has been provided from the parent's SC fetch. This ensures MUI Select
  // initialises with both a valid value AND a populated options list — preventing
  // the "no option selected" state on back-navigation (e.g. value="BOTH" with
  // an empty options array renders blank because there is no matching option yet).
  if (loading) return null;

  const submittedFromUi = detailsData?.submittedFromUi;
  const isApi = submittedFromUi === 'No' || submittedFromUi === 'no' || submittedFromUi === false;

  return (
    <Box>
      {SC_ID && isApi && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="warning" variant="outlined" sx={{ border: "2px solid #ed6c02", borderRadius: 2 }}>
            <AlertTitle sx={{ fontWeight: 'bold' }}>Data Source: API Submission</AlertTitle>
            This Scope Certificate was submitted via the API. Certain features, such as <strong>Word document generation</strong>, are restricted and unavailable for this certificate.
          </Alert>
        </Box>
      )}
      <FormBuilderV4
        formConfig={formConfig}
        onSubmit={formConfig.handleSubmit}
        formRef={formRef}
      />
    </Box>
  );
};

export default ScopeCertificateDetailsForm;
